import { Injectable, signal, effect, computed, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type ThemeMode = 'light' | 'dark' | 'system';

@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private platformId = inject(PLATFORM_ID);
    private isBrowser = isPlatformBrowser(this.platformId);

    // Signal to store the user preference
    private themeModeSignal = signal<ThemeMode>(
        (this.isBrowser ? (localStorage.getItem('theme-preference') as ThemeMode) : 'system') || 'system'
    );

    // Readonly signal for external components
    readonly themeMode = this.themeModeSignal.asReadonly();

    // Computed signal to determine if we are currently in dark mode (resolved system preference)
    readonly isDarkMode = computed(() => {
        const mode = this.themeModeSignal();
        if (mode === 'system' && this.isBrowser) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return mode === 'dark';
    });

    constructor() {
        // Apply theme changes automatically
        effect(() => {
            const isDark = this.isDarkMode();
            const mode = this.themeModeSignal();

            if (this.isBrowser) {
                // Persist the preference
                localStorage.setItem('theme-preference', mode);

                // Apply/remove .dark-theme class from html element
                if (isDark) {
                    document.documentElement.classList.add('dark-theme');
                } else {
                    document.documentElement.classList.remove('dark-theme');
                }

                // Update favicon dynamically
                const favicon = document.getElementById('app-favicon') as HTMLLinkElement | null;
                if (favicon) {
                    favicon.href = isDark ? 'Assets/icons/Myservy Icon white.png' : 'Assets/icons/Myservy Icon black.png';
                }
            }
        });

        if (this.isBrowser) {
            // Listen for system theme changes
            window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
                if (this.themeModeSignal() === 'system') {
                    // Trigger a re-calculation of computed signals
                    this.themeModeSignal.set('system');
                }
            });
        }
    }

    setThemeMode(mode: ThemeMode) {
        this.themeModeSignal.set(mode);
    }

    toggleTheme() {
        const current = this.themeModeSignal();
        if (current === 'light') {
            this.setThemeMode('dark');
        } else {
            this.setThemeMode('light');
        }
    }
}